import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
  tableCellClasses,
} from '@mui/material';
import { errorNotification } from '../../reducers/handle-api-status/slice';
import { ErrorAlert } from '../../components/atoms/Alert-message';
import {
  SiteReviewTitle,
  SiteReviewValue,
  TextAsLink,
  boldFont,
  homeButtonComponent,
  lscDescriptionValue,
  titleName,
} from '../../styles';
import { APIHeader } from '../../index';
import { getDownloadLink } from '../../utils';

const EstimateReview = props => {
  const { apiConfig, pecrURLData, errorInAPI } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [estimateData, setEstimateData] = useState([]);
  const [fetchedFiles, setFetchedFiles] = useState([]);

  useEffect(() => {
    if ((pecrURLData.pecrId, pecrURLData.siteId)) {
      fetch(`${apiConfig.uiURL}api/FetchAllDepartmentStatus`, {
        method: 'POST',
        headers: APIHeader(),
        body: JSON.stringify({
          pecrId: pecrURLData.pecrId,
          siteId: pecrURLData.siteId,
        }),
      })
        .then(async response => {
          const data = await response.json();
          setEstimateData(data);
          data.map(item =>
            fetch(`${apiConfig.uiURL}api/FetchFilesFromDepartment`, {
              method: 'POST',
              headers: APIHeader(),
              body: JSON.stringify({
                MainFolder: 'PECROverview',
                SubFolder: item.PECR_ID,
                siteId: item.SiteID,
                departmentId: item.DeptId,
                index: item.EDIndex,
              }),
            })
              .then(async response => {
                const data2 = await response.json();
                setFetchedFiles(prevState => [
                  ...prevState,
                  {
                    documents: data2,
                    departmentId: item.DeptId,
                    index: item.EDIndex,
                  },
                ]);
              })
              .catch(() => {
                dispatch(
                  errorNotification({
                    hasErrorOccured: true,
                    message: t('error_message'),
                  })
                );
              })
          );
        })
        .catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pecrURLData.pecrId, pecrURLData.siteId, apiConfig.uiURL]);

  useEffect(() => {
    const finalData = estimateData.map(item => {
      const item2 = fetchedFiles.find(
        value =>
          value.departmentId === item.DeptId && value.index === item.EDIndex
      );
      return item2 ? { ...item, Doc: item2.documents } : item;
    });

    setEstimateData(finalData);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedFiles]);

  const onBackButtonClick = () => {
    window.history.back();
  };

  //To download the files on DownloadFile API click
  const onDownloadFileClick = (
    pecrId,
    siteId,
    departmentId,
    eIndex,
    filename
  ) => {
    fetch(`${apiConfig.uiURL}api/DownloadFile`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        MainFolder: 'PECROverview',
        SubFolder: pecrId,
        siteId: siteId,
        departmentId: departmentId,
        index: eIndex,
        filename: filename,
      }),
    })
      .then(async response => {
        response.blob().then(blob => {
          getDownloadLink(blob, filename);
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: '#1F305E',
      color: theme.palette.common.white,
      fontWeight: 'bold',
      fontSize: '17px',
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: '17px',
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.action.hover,
    },
  }));
  return errorInAPI.hasErrorOccured ? (
    <ErrorAlert message={errorInAPI.message} />
  ) : estimateData.length > 0 ? (
    <Grid>
      <Grid style={SiteReviewTitle}>
        <h4 style={titleName}>{t('PECR_Site_Estimate_Summary')}</h4>
      </Grid>
      <Grid style={SiteReviewTitle}>
        <Grid style={boldFont}>{t('Title')}:</Grid>
        <Grid style={lscDescriptionValue}>
          {(estimateData &&
            estimateData[0].Title &&
            pecrURLData.pecrId &&
            `${estimateData[0].Title}(${pecrURLData.pecrId})`) ||
            ''}
        </Grid>
      </Grid>

      <Grid style={SiteReviewTitle}>
        <Grid style={boldFont}>{t('Description')}:</Grid>
        <Grid style={lscDescriptionValue}>
          {(estimateData && estimateData[0].Description) || ''}
        </Grid>
      </Grid>
      <Grid style={SiteReviewTitle}>
        <Grid style={boldFont}>{t('Product_Family')}:</Grid>
        <Grid style={SiteReviewValue}>
          {(estimateData &&
            estimateData[0].ProductFamily &&
            estimateData[0].ProductFamily.split(',').join(', ')) ||
            ''}
        </Grid>
      </Grid>
      <Grid style={SiteReviewTitle}>
        <Grid style={boldFont}>{t('Creator')}:</Grid>
        <Grid style={SiteReviewValue}>
          {(estimateData &&
            estimateData[0].CreatorFirstName &&
            estimateData[0].CreatorLastName &&
            estimateData[0].CreatorSiteName &&
            `${estimateData[0].CreatorFirstName} ${estimateData[0].CreatorLastName} (${estimateData[0].CreatorSiteName})`) ||
            ''}
        </Grid>
      </Grid>
      <Grid style={SiteReviewTitle}>
        <Grid style={boldFont}>{t('Owner')}:</Grid>
        <Grid style={SiteReviewValue}>
          {(estimateData &&
            estimateData[0].OwnerFirstName &&
            estimateData[0].OwnerLastName &&
            `${estimateData[0].OwnerFirstName} ${estimateData[0].OwnerLastName}`) ||
            ''}
        </Grid>
      </Grid>

      <Grid style={SiteReviewTitle}>
        <Grid style={boldFont}>{t('Site_Name')}:</Grid>
        <Grid style={SiteReviewValue}>
          {(estimateData && estimateData[0].SiteName) || ''}
        </Grid>
      </Grid>
      <Grid style={SiteReviewTitle}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 100 }} aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell align="center" width="10%">
                  {t('Department')}
                </StyledTableCell>
                <StyledTableCell align="center" width="10%">
                  {t('Status')}
                </StyledTableCell>
                <StyledTableCell align="center" width="10%">
                  {t('Lead')}
                </StyledTableCell>
                <StyledTableCell align="center" width="10%">
                  {t('Date')}
                </StyledTableCell>
                <StyledTableCell align="center" width="10%">
                  {t('Effort_Hours')}
                </StyledTableCell>
                <StyledTableCell align="center" width="10%">
                  {t('Costs_Currency')}
                </StyledTableCell>
                <StyledTableCell align="center" width="10%">
                  {t('Comments')}
                </StyledTableCell>
                <StyledTableCell align="center" width="10%">
                  {t('Attachements')}
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estimateData.map((data, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell align="center" width="10%">
                    {data && data.Dept ? data.Dept : ''}
                  </StyledTableCell>
                  <StyledTableCell align="center" width="10%">
                    {data && data.StatusName ? data.StatusName : ''}
                  </StyledTableCell>
                  <StyledTableCell align="center" width="10%">
                    {`${data && data.LeadFirstName ? data.LeadFirstName : ''} ${
                      data && data.LeadLastName ? data.LeadLastName : ''
                    }`}
                  </StyledTableCell>
                  <StyledTableCell align="center" width="10%">
                    {data && data.EstimatedDate ? data.EstimatedDate : ''}
                  </StyledTableCell>
                  <StyledTableCell align="center" width="10%">
                    {data &&
                    data.EstimatedEffort &&
                    data.EstimatedEffort.toString() === '0'
                      ? data.EstimatedEffort
                      : ''}
                  </StyledTableCell>
                  <StyledTableCell align="center" width="10%">
                    {data &&
                    data.EstimatedCost &&
                    data.EstimatedCost.toString() === '0'
                      ? data.EstimatedCost
                      : ''}
                  </StyledTableCell>
                  <StyledTableCell align="center" width="10%">
                    <Tooltip
                      title={
                        data && data.EstimatedComment
                          ? data.EstimatedComment
                          : ''
                      }
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontSize: '14px',
                          },
                        },
                      }}
                    >
                      <Typography noWrap width={150} sx={{ cursor: 'default' }}>
                        {data && data.EstimatedComment
                          ? data.EstimatedComment
                          : ''}
                      </Typography>
                    </Tooltip>
                  </StyledTableCell>
                  <StyledTableCell align="center" width="10%">
                    {data && data.Doc.length > 0
                      ? data.Doc.map((document, id) => {
                          return (
                            <Tooltip
                              key={id}
                              title={document}
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    fontSize: '14px',
                                  },
                                },
                              }}
                            >
                              <Typography
                                key={id}
                                noWrap
                                width={300}
                                style={TextAsLink}
                                onClick={() =>
                                  onDownloadFileClick(
                                    data.PECR_ID,
                                    data.SiteID,
                                    data.DeptId,
                                    data.EDIndex,
                                    document
                                  )
                                }
                              >
                                {document}
                              </Typography>
                            </Tooltip>
                          );
                        })
                      : ''}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid style={homeButtonComponent}>
        <Button onClick={onBackButtonClick} variant="contained">
          {t('Back')}
        </Button>
      </Grid>
    </Grid>
  ) : (
    <Grid style={homeButtonComponent}>
      <CircularProgress />
    </Grid>
  );
};

const mapStateToProps = (state = {}) => {
  return {
    pecrURLData: state.pecrURLData,
    errorInAPI: state.errorNotification,
  };
};

export default connect(mapStateToProps, null)(EstimateReview);
